import { documentsApi } from 'app/services/api';
import _ from 'lodash';

import { jsonStringify } from 'utils/json';
import { getUUID } from 'utils/uuid';

import { MANUAL_CAPTURE_FLAG } from './DocumentCaptureSession.constants';

/** Object-URL creation guarded for non-browser test environments (jsdom). */
const createPreviewUrl = file => {
  if (!_.isFunction(URL?.createObjectURL)) {
    return null;
  }

  return URL.createObjectURL(file);
};

/** Releases a preview object URL created by {@link createPreviewUrl}. */
export const revokePreviewUrl = previewUrl => {
  if (previewUrl && _.isFunction(URL?.revokeObjectURL)) {
    URL.revokeObjectURL(previewUrl);
  }
};

/** Strips a trailing extension for the default editable document name. */
const stripExtension = fileName => fileName.replace(/\.[^./\\]+$/, '');

/**
 * Wraps a captured/selected File into a batch record.
 *
 * @param {File} file
 * @param {Object} [meta]
 * @param {string|null} [meta.nativePath] device temp path (native multi-shot only)
 * @returns {{ id: string, file: File, name: string, previewUrl: string|null, nativePath: string|null }}
 */
export const createCaptureDocument = (file, { nativePath = null } = {}) => ({
  id: getUUID(),
  file,
  name: stripExtension(file.name),
  previewUrl: createPreviewUrl(file),
  nativePath,
});

/** Builds batch records from a FileList / File[] (web multi-select path). */
export const createCaptureDocumentsFromFiles = files =>
  Array.from(files ?? []).map(file => createCaptureDocument(file));

/**
 * Upload FormData for a single captured document, matching the existing
 * `documentsApi.uploadDocument` contract (mirrors AiBatchIntake) — a multipart
 * body of `file` + a JSON `body` whose `Metadata.ApplicationId` is the target.
 */
const buildUploadFormData = (document, applicationId) => {
  const fileName = `${document.name || 'document'}.${
    document.file.name.split('.').pop() ?? 'jpeg'
  }`;

  const body = {
    FileName: fileName,
    ContentType: document.file.type,
    Metadata: {
      ApplicationId: applicationId,
      IsManuallyCreated: MANUAL_CAPTURE_FLAG,
    },
  };

  const formData = new FormData();

  formData.append('file', document.file, fileName);
  formData.append('body', jsonStringify(body));

  return formData;
};

/**
 * Uploads the captured batch against a target application. This is the reuse of
 * the DEV-4250 upload→server-side-extraction path; uploading requires a target
 * `applicationId`, which the downstream draft-create flow supplies (the
 * integration seam — see DocumentCaptureSession `onSubmit`).
 *
 * @returns {Promise<{ fulfilledIds: string[], failedCount: number }>}
 */
export const uploadCaptureBatch = async (documents, applicationId) => {
  const results = await Promise.allSettled(
    documents.map(document =>
      documentsApi
        .uploadDocument(buildUploadFormData(document, applicationId))
        .then(() => document.id),
    ),
  );

  return {
    fulfilledIds: results
      .filter(result => result.status === 'fulfilled')
      .map(result => result.value),
    failedCount: results.filter(result => result.status === 'rejected').length,
  };
};
